// ============================================================================
// ContactActions.tsx — ЕДИНЫЕ действия карточки контакта (SSoT/SRP).
// Telegram/MAX — ТОЛЬКО при реальной привязке adapter_links; всегда доступны
// внутренний чат и профиль. Доступность канала определяет сервис контактов
// (contactService), UI лишь показывает действия.
// ============================================================================

import { MessageCircle, MessagesSquare, Send, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ContactAction, ContactChannelKind, ContactDetails } from '@/lib/contacts/types';
import { contactActions } from '@/lib/contacts/contactService';

interface ContactActionsProps {
  details: ContactDetails;
  busyKind?: string;
  onAction: (action: ContactAction) => void;
  /** Компактный режим — кнопки одной строкой (карточка в панели). */
  compact?: boolean;
  /** Скрыть «Открыть профиль» — когда карточка уже показана в профиле. */
  showProfile?: boolean;
}

function ActionIcon({ kind }: { kind: ContactChannelKind | 'profile' }) {
  if (kind === 'telegram') return <Send className="h-3.5 w-3.5" />;
  if (kind === 'max') return <MessageCircle className="h-3.5 w-3.5" />;
  if (kind === 'internal') return <MessagesSquare className="h-3.5 w-3.5" />;
  return <UserRound className="h-3.5 w-3.5" />;
}

export function ContactActions({ details, busyKind = '', onAction, compact = false, showProfile = true }: ContactActionsProps) {
  const actions = contactActions(details).filter((action) => showProfile || action.kind !== 'profile');
  // Причина недоступности — ТЕКСТОМ (не только title): на мобильном подсказки
  // по наведению нет, а пользователь должен понимать, почему нельзя написать.
  const blocked = actions.filter(
    (action) => !action.available && action.hint && (action.kind === 'telegram' || action.kind === 'max'),
  );
  return (
    <div className={`flex flex-wrap gap-1.5 ${compact ? '' : 'pt-1'}`}>
      {actions.map((action) => {
        const busy = busyKind === action.kind;
        const label = busy ? 'Открываю…' : action.label;
        return (
          <Button
            key={action.kind}
            type="button"
            size="sm"
            variant={action.kind === 'internal' ? 'default' : 'outline'}
            className="h-8 px-2.5 text-[11px]"
            disabled={!action.available || !!busyKind}
            title={action.available ? action.label : action.hint}
            onClick={() => onAction(action)}
          >
            <span className="mr-1.5 inline-flex"><ActionIcon kind={action.kind} /></span>
            {label}
          </Button>
        );
      })}
      {blocked.length > 0 && (
        <div className="w-full space-y-0.5 text-[10px] leading-snug text-muted-foreground">
          {blocked.map((action) => (
            <div key={action.kind} className="break-words">{action.hint}</div>
          ))}
        </div>
      )}
    </div>
  );
}
